import { arabicFonts, latinFonts } from './tokens';

/**
 * Typography presets — the same closed set the app bundles
 * (`patient-app/src/theme/fontPresets.ts`; authority is
 * `backend/src/lib/patient-app-branding.ts` FONT_PRESET_KEYS). On the web the
 * faces come from Google Fonts: the default pairing plus Plex Arabic are linked
 * in `index.html`; a tenant preset is loaded on demand by ThemeProvider.
 */

export const FONT_PRESET_KEYS = ['instrument', 'manrope', 'source', 'inter', 'lora', 'space'] as const;
export type FontPresetKey = (typeof FONT_PRESET_KEYS)[number];
export const FONT_PRESET_DEFAULT: FontPresetKey = 'instrument';

export type FontRoles = typeof latinFonts;

type FontPreset = { fonts: FontRoles; googleFamilies: string[] };

const PRESETS: Record<FontPresetKey, FontPreset> = {
  instrument: { fonts: latinFonts, googleFamilies: [] },
  manrope: {
    fonts: { sans: "'Manrope', system-ui, sans-serif", serif: "'Manrope', system-ui, sans-serif", serifWeight: '700' },
    googleFamilies: ['Manrope:wght@500;600;700;800'],
  },
  source: {
    fonts: {
      sans: "'Source Sans 3', system-ui, sans-serif",
      serif: "'Source Serif 4', Georgia, serif",
      serifWeight: '400',
    },
    googleFamilies: ['Source+Sans+3:wght@500;600;700', 'Source+Serif+4:wght@400'],
  },
  inter: {
    fonts: { sans: "'Inter', system-ui, sans-serif", serif: "'Inter', system-ui, sans-serif", serifWeight: '600' },
    googleFamilies: ['Inter:wght@500;600;700'],
  },
  lora: {
    fonts: { sans: "'Source Sans 3', system-ui, sans-serif", serif: "'Lora', Georgia, serif", serifWeight: '500' },
    googleFamilies: ['Source+Sans+3:wght@500;600;700', 'Lora:wght@500'],
  },
  space: {
    fonts: {
      sans: "'Space Grotesk', system-ui, sans-serif",
      serif: "'Space Grotesk', system-ui, sans-serif",
      serifWeight: '700',
    },
    googleFamilies: ['Space+Grotesk:wght@500;600;700'],
  },
};

export function isFontPresetKey(value: unknown): value is FontPresetKey {
  return typeof value === 'string' && (FONT_PRESET_KEYS as readonly string[]).includes(value);
}

/** RTL always wins: no Latin preset has Arabic coverage. */
export function fontsForPreset(preset: FontPresetKey | null | undefined, direction: 'ltr' | 'rtl'): FontRoles {
  if (direction === 'rtl') return arabicFonts;
  return (preset && PRESETS[preset] ? PRESETS[preset] : PRESETS[FONT_PRESET_DEFAULT]).fonts;
}

/** Google Fonts stylesheet URL for a preset, or null when nothing extra is needed. */
export function fontPresetStylesheetUrl(preset: FontPresetKey | null | undefined): string | null {
  const families = preset && PRESETS[preset] ? PRESETS[preset].googleFamilies : [];
  if (!families.length) return null;
  return `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap`;
}
